import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SESSION_IDLE_TIMEOUT_MS, SESSION_IDLE_WARNING_MS } from "@telecurb/shared";
import { useSignOut } from "@/auth/mutations";
import { api, isApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export const LAST_ACTIVITY_KEY = "tc-last-activity";
const ACTIVITY_WRITE_THROTTLE_MS = 30_000;
const ACTIVITY_EVENTS = [
  "pointerdown",
  "keydown",
  "wheel",
  "touchstart",
  "mousemove",
  "scroll",
] as const;
// Idle elapsed at which the warning modal appears (SESSION_IDLE_WARNING_MS before logout).
const WARN_AT_MS = SESSION_IDLE_TIMEOUT_MS - SESSION_IDLE_WARNING_MS;
const TICK_MS = 1_000;

function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

/**
 * Watches for inactivity and warns the user before the server-enforced idle
 * timeout drops their session. The server cookie's rolling `maxAge` is the real
 * enforcement (see spec §11); these timers are UX only. Mounted once inside
 * `AppShell` so it runs only on authenticated routes.
 */
export function IdleTimeout() {
  const navigate = useNavigate();
  const signOut = useSignOut();

  const [warning, setWarning] = useState(false);
  const [remainingMs, setRemainingMs] = useState(SESSION_IDLE_WARNING_MS);
  const [extendError, setExtendError] = useState<string | null>(null);
  const [extending, setExtending] = useState(false);

  const lastActivityRef = useRef<number>(Date.now());
  const lastWriteRef = useRef<number>(0);
  // Mirror state/props into refs so the interval and event listeners can stay
  // subscribed once (empty-dep effects) without reading stale closures.
  const warningRef = useRef(warning);
  warningRef.current = warning;
  const signedOutRef = useRef(false);
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // Seed the activity anchor from a *recent* cross-tab value, else stamp now.
  // A stored value older than the idle window is stale from a previous session
  // (it's never cleared on logout) — adopting it would make the first tick read
  // the user as already idle and force an instant sign-out on mount. Only trust
  // a timestamp that's still inside the active window; otherwise re-anchor now.
  useEffect(() => {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
    const stored = raw ? Number(raw) : NaN;
    const now = Date.now();
    if (Number.isFinite(stored) && now - stored < SESSION_IDLE_TIMEOUT_MS) {
      lastActivityRef.current = stored;
    } else {
      lastActivityRef.current = now;
      lastWriteRef.current = now;
      localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    }
  }, []);

  // Record local activity. Ignored while the warning modal is open: extending
  // then must go through "Stay signed in" so the server cookie is actually
  // rolled (a bare pointer event does not hit the API). localStorage writes are
  // throttled to once per 30 s; the in-memory anchor updates every time.
  useEffect(() => {
    function recordActivity() {
      if (warningRef.current) return;
      const now = Date.now();
      lastActivityRef.current = now;
      if (now - lastWriteRef.current >= ACTIVITY_WRITE_THROTTLE_MS) {
        lastWriteRef.current = now;
        localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
      }
    }
    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, recordActivity, { passive: true });
    }
    return () => {
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, recordActivity);
      }
    };
  }, []);

  // Cross-tab activity resets the countdown in every tab.
  useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (event.key !== LAST_ACTIVITY_KEY || event.newValue == null) return;
      const stamp = Number(event.newValue);
      if (!Number.isFinite(stamp)) return;
      lastActivityRef.current = stamp;
      if (Date.now() - stamp < WARN_AT_MS) {
        setWarning(false);
        setExtendError(null);
      }
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Shared by the ticking clock and the failed-extend path (only reads stable
  // refs, so the empty-dep callback never goes stale).
  const signOutForIdle = useCallback(() => {
    if (signedOutRef.current) return;
    signedOutRef.current = true;
    // Navigate regardless of the mutation outcome, mirroring AppShell's
    // sign-out error tolerance.
    signOutRef.current
      .mutateAsync()
      .catch(() => {})
      .finally(() => {
        navigateRef.current("/sign-in", { replace: true, state: { reason: "idle" } });
      });
  }, []);

  // Single ticking clock derives modal visibility and forced sign-out.
  useEffect(() => {
    function tick() {
      if (signedOutRef.current) return;
      const idle = Date.now() - lastActivityRef.current;
      const remaining = SESSION_IDLE_TIMEOUT_MS - idle;
      if (remaining <= 0) {
        signOutForIdle();
        return;
      }
      if (idle >= WARN_AT_MS) {
        setWarning(true);
        setRemainingMs(remaining);
      } else {
        setWarning(false);
      }
    }
    const id = setInterval(tick, TICK_MS);
    tick();
    return () => clearInterval(id);
  }, [signOutForIdle]);

  async function handleStay() {
    setExtending(true);
    setExtendError(null);
    try {
      // GET /me rides the same requireAuth path that re-sets tc_session, so a
      // successful call rolls the server cookie. Only then reset the timer.
      await api("/api/v1/me");
      const now = Date.now();
      lastActivityRef.current = now;
      lastWriteRef.current = now;
      localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
      setWarning(false);
      setExtendError(null);
    } catch (err) {
      // A 401 means the server session is already gone (requireAuth could not
      // refresh it) — extending can never succeed, so don't strand the user in
      // the modal (TEL-247). Send them through the idle sign-out path, which
      // lands on /sign-in with the "signed out due to inactivity" banner.
      if (isApiError(err) && err.status === 401) {
        signOutForIdle();
        return;
      }
      // Transient failure (network, 5xx): never silently reset the timer —
      // keep the modal open with the error so the user can retry.
      setExtendError("Could not extend session.");
    } finally {
      setExtending(false);
    }
  }

  return (
    <Dialog open={warning}>
      <DialogContent
        className="[&>button]:hidden sm:max-w-sm"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Are you still there?</DialogTitle>
          <DialogDescription>
            You&rsquo;ll be signed out due to inactivity in{" "}
            <span className="font-medium tabular-nums">{formatRemaining(remainingMs)}</span>.
          </DialogDescription>
        </DialogHeader>
        {extendError && (
          <p role="alert" className="text-sm text-destructive">
            {extendError}
          </p>
        )}
        <DialogFooter>
          <Button onClick={handleStay} disabled={extending}>
            {extending ? "Staying signed in…" : "Stay signed in"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
